/**
 * Sales Team Page
 * Displays the sales hierarchy with tree view and list view
 */

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getSalesPositions,
  buildHierarchyTree,
} from "../services/salesTeam.service";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Badge from "../components/ui/Badge";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  UserCircleIcon,
  ArrowsRightLeftIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import type {
  SalesPosition,
  SalesPositionType,
  SalesHierarchyNode,
  SalesPositionFilters,
} from "../types/sales.types";
import { POSITION_TYPE_LABELS } from "../types/sales.types";
import {
  addMonths,
  centralToday,
  monthStart,
  getTeamAvailability,
  monthLabel,
} from "../services/msrFeedback.service";

// Business lead, 2026-10-27: managers see each MSR's AVAILABILITY for next month (the only month reps fill in) --
// "Nov: 8 days" or "Nov: not entered". Replaces "calendar confirmed through".
// One shared query for every row (get_msr_team_availability lists exactly the active MSR positions).
const RepCalendarStatus: React.FC<{ position: SalesPosition }> = ({
  position,
}) => {
  const today = centralToday();
  // Owner R2: reps fill in only the NEXT full month; show that month's count.
  const month = addMonths(monthStart(today), 1);
  const { data } = useQuery({
    queryKey: ["msr-team-availability", month],
    queryFn: () => getTeamAvailability(month),
    retry: false,
    staleTime: 60_000,
  });
  const row = data?.find((r) => r.sales_position_id === position.id);
  if (!row || !position.is_active) {
    return null;
  }
  const short = monthLabel(month).split(" ")[0].slice(0, 3);
  return row.days.length ? (
    <span
      className="inline-flex items-center rounded bg-green-100 px-1.5 py-0.5 text-[11px] font-medium text-green-800"
      title={`Available ${row.days.length} day(s) in ${monthLabel(month)}`}
    >
      {short}: {row.days.length} day{row.days.length === 1 ? "" : "s"}
    </span>
  ) : (
    <span
      className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800"
      title={`No availability entered for ${monthLabel(month)}`}
    >
      {short}: not entered
    </span>
  );
};

// Badge color mapping for position types
const positionTypeColors: Record<SalesPositionType, string> = {
  vp: "purple",
  director: "blue",
  regional_manager: "cyan",
  territory_manager: "green",
  sales_rep: "gray",
};

// Indentation colors for tree lines
const depthColors = [
  "border-purple-300",
  "border-blue-300",
  "border-cyan-300",
  "border-green-300",
  "border-gray-300",
];

interface TreeNodeProps {
  node: SalesHierarchyNode;
  onSelect: (position: SalesPosition) => void;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  onSelect,
  expandedIds,
  onToggle,
}) => {
  const { position, children, depth } = node;
  const isExpanded = expandedIds.has(position.id);
  const hasChildren = children.length > 0;
  const displayName =
    position.assigned_profile?.full_name ||
    position.assigned_name ||
    "Unassigned";

  return (
    <div>
      <div
        className={`flex items-center py-2 px-3 hover:bg-gray-50 cursor-pointer rounded-lg group transition-colors ${
          depth > 0 ? "ml-" + Math.min(depth * 6, 24) : ""
        }`}
        style={{ marginLeft: depth > 0 ? `${depth * 1.5}rem` : undefined }}
        onClick={() => onSelect(position)}
      >
        {/* Expand/collapse toggle */}
        <div className="w-6 flex-shrink-0">
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle(position.id);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
          ) : (
            <span className="inline-block w-4" />
          )}
        </div>

        {/* Depth indicator line */}
        {depth > 0 && (
          <div
            className={`w-0.5 h-6 ${depthColors[Math.min(depth - 1, depthColors.length - 1)]} border-l-2 mr-2 -ml-1`}
          />
        )}

        {/* Avatar / icon */}
        <div className="flex-shrink-0 mr-3">
          <UserCircleIcon className="h-8 w-8 text-gray-300" />
        </div>

        {/* Position info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 truncate">
              {displayName}
            </span>
            <Badge
              color={positionTypeColors[position.position_type] || "gray"}
              size="sm"
            >
              {POSITION_TYPE_LABELS[position.position_type] ||
                position.position_type}
            </Badge>
            {!position.is_active && (
              <Badge color="red" size="sm">
                Inactive
              </Badge>
            )}
            {position.is_part_time && (
              <Badge color="yellow" size="sm">
                PT
              </Badge>
            )}
            <RepCalendarStatus position={position} />
          </div>
          <div className="text-xs text-gray-500 truncate">
            {position.title}
            {position.territory && ` · ${position.territory}`}
            {position.region && ` · ${position.region}`}
          </div>
        </div>

        {/* Right side: child count */}
        {hasChildren && (
          <span className="text-xs text-gray-400 ml-2">
            {children.length} report{children.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Children */}
      {isExpanded &&
        children.map((child) => (
          <TreeNode
            key={child.position.id}
            node={child}
            onSelect={onSelect}
            expandedIds={expandedIds}
            onToggle={onToggle}
          />
        ))}
    </div>
  );
};

const SalesTeam: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (user?.role ?? "").toLowerCase().replace(/[_-]/g, "");
  const canManageAdSync =
    role === "admin" || role === "superadmin" || role === "it";
  const canManagePromotionRules = role === "admin" || role === "superadmin";
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"tree" | "list">("tree");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Debounce search
  React.useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Build filters
  const filters: SalesPositionFilters = {
    search: searchQuery || undefined,
    position_type:
      typeFilter !== "all" ? [typeFilter as SalesPositionType] : undefined,
    is_active: true,
  };

  const {
    data: positions,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["sales-positions", filters],
    queryFn: () => getSalesPositions(filters),
  });

  // Build hierarchy tree (only for tree view, unfiltered)
  const allPositionsQuery = useQuery({
    queryKey: ["sales-positions-all"],
    queryFn: () => getSalesPositions({ is_active: true }),
    enabled: viewMode === "tree",
  });

  const hierarchyTree = useMemo(() => {
    if (!allPositionsQuery.data) {
      return [];
    }
    return buildHierarchyTree(allPositionsQuery.data);
  }, [allPositionsQuery.data]);

  // Auto-expand top levels on first load
  React.useEffect(() => {
    if (hierarchyTree.length > 0 && expandedIds.size === 0) {
      const topIds = new Set<string>();
      // Expand VP and Director levels
      for (const root of hierarchyTree) {
        topIds.add(root.position.id);
        for (const child of root.children) {
          topIds.add(child.position.id);
        }
      }
      setExpandedIds(topIds);
    }
  }, [hierarchyTree]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAll = () => {
    if (!allPositionsQuery.data) {
      return;
    }
    setExpandedIds(new Set(allPositionsQuery.data.map((p) => p.id)));
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  const handleSelect = (position: SalesPosition) => {
    navigate(`/sales-team/${position.id}`);
  };

  // Stats
  const stats = useMemo(() => {
    const all = allPositionsQuery.data || positions || [];
    const counts: Record<string, number> = {};
    for (const p of all) {
      counts[p.position_type] = (counts[p.position_type] || 0) + 1;
    }
    return counts;
  }, [allPositionsQuery.data, positions]);

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          Failed to load sales team data. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Team</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage the sales hierarchy, territories, and team assignments
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManageAdSync && (
            <Button
              variant="secondary"
              onClick={() => navigate("/admin/ad-sync")}
              title="Review hierarchy proposals from Microsoft Entra"
            >
              <ArrowsRightLeftIcon className="h-4 w-4 mr-1" />
              AD / Entra Sync
            </Button>
          )}
          {canManagePromotionRules && (
            <Button
              variant="secondary"
              onClick={() => navigate("/admin/promotion-rules")}
              title="Manage state → promotion auto-assign rules"
            >
              Promotion Rules
            </Button>
          )}
          <Button onClick={() => navigate("/sales-team/create")}>
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Position
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {(
          [
            "vp",
            "director",
            "regional_manager",
            "territory_manager",
            "sales_rep",
          ] as SalesPositionType[]
        ).map((type) => (
          <div
            key={type}
            className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:border-gray-400 transition-colors"
            onClick={() => setTypeFilter(typeFilter === type ? "all" : type)}
          >
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              {type === "vp"
                ? "VPs of Sales"
                : `${POSITION_TYPE_LABELS[type]}s`}
            </div>
            <div className="text-2xl font-bold text-gray-900 mt-1">
              {stats[type] || 0}
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, title, territory..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Type filter */}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All Levels</option>
          <option value="vp">VP</option>
          <option value="director">Directors</option>
          <option value="regional_manager">Regional Managers</option>
          <option value="territory_manager">Territory Managers</option>
          <option value="sales_rep">Sales Reps</option>
        </select>

        {/* View toggle */}
        <div className="flex border border-gray-300 rounded-lg overflow-hidden">
          <button
            className={`px-3 py-2 text-sm ${viewMode === "tree" ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-600 hover:bg-gray-50"}`}
            onClick={() => setViewMode("tree")}
          >
            Hierarchy
          </button>
          <button
            className={`px-3 py-2 text-sm border-l border-gray-300 ${viewMode === "list" ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-600 hover:bg-gray-50"}`}
            onClick={() => setViewMode("list")}
          >
            List
          </button>
        </div>

        {/* Expand/Collapse (tree view only) */}
        {viewMode === "tree" && (
          <div className="flex gap-1">
            <button
              onClick={expandAll}
              className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1"
            >
              Collapse All
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {isLoading || allPositionsQuery.isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : viewMode === "tree" ? (
        /* Tree View */
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          {hierarchyTree.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <UserCircleIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="font-medium">No positions defined yet</p>
              <p className="text-sm mt-1">
                Start by adding the VP of Sales position
              </p>
              <Button
                className="mt-4"
                onClick={() => navigate("/sales-team/create")}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Position
              </Button>
            </div>
          ) : (
            hierarchyTree.map((node) => (
              <TreeNode
                key={node.position.id}
                node={node}
                onSelect={handleSelect}
                expandedIds={expandedIds}
                onToggle={toggleExpand}
              />
            ))
          )}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Position
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Level
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Territory / Region
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(positions || []).map((pos) => (
                <tr
                  key={pos.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleSelect(pos)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <UserCircleIcon className="h-6 w-6 text-gray-300 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900">
                        {pos.assigned_profile?.full_name ||
                          pos.assigned_name ||
                          "Unassigned"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {pos.title}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      color={positionTypeColors[pos.position_type] || "gray"}
                      size="sm"
                    >
                      {POSITION_TYPE_LABELS[pos.position_type]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {[pos.territory, pos.region].filter(Boolean).join(" · ") ||
                      "—"}
                  </td>
                  <td className="px-4 py-3 space-x-1">
                    <RepCalendarStatus position={pos} />
                    {pos.is_active ? (
                      <Badge color="green" size="sm">
                        Active
                      </Badge>
                    ) : (
                      <Badge color="red" size="sm">
                        Inactive
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
              {(positions || []).length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-12 text-center text-gray-500"
                  >
                    No positions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SalesTeam;
